import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getSurveySchema, getSurveySchemas } from '../../api';
import { langType, publicationStatusValues } from '../../constants/types';
import ModalDialog from '../ModalDialog';
import LabelWrapper from '../LabelWrapper';
import { Select } from '../Select';
import {
  addErrorMessageAction,
  clearSurveyAction,
  setTaskFulfilled,
  setTaskPending,
  updateSurveyOptionsAction
} from '../../actions';
import {
  getSubRouteForSurveyType, getTitlePeriods, getLocalizedText, isSurveyForDegreeProgram
} from '../../util';
import { getLocalizedResearchAgreement, getMessages, withLang } from '../../i18n';

const VERY_LONG_PAGE = 999;

const SelectSchemaDialog = ({
  schemaType,
  children,
  translate: t,
  lang,
  dispatchUpdate,
  dispatchAddErrorMessage,
  dispatchSetTaskFulfilled,
  dispatchSetTaskPending,
  dispatchClearSurvey
}) => {
  const navigate = useNavigate();
  const [schemas, setSchemas] = React.useState([]);
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedSchemaId, setSelectedSchemaId] = React.useState();

  React.useEffect(() => {
    getSurveySchemas(0, VERY_LONG_PAGE, [publicationStatusValues.PUBLISHED])
      .then((result) => setSchemas(result.content.filter((surveySchema) => surveySchema.type === schemaType)));
  }, [getSurveySchemas, setSchemas]);

  const handleCancel = () => setIsOpen(false);
  const handleConfirm = () => {
    dispatchClearSurvey();
    dispatchSetTaskPending();
    getSurveySchema(selectedSchemaId)
      .then((schema) => {
        const {
          id: schemaId,
          title,
          introduction,
          type: surveyType,
          invitationEmailTitle,
          invitationEmailContent,
          version: schemaVersion
        } = schema;
        const schemaTitle = title;
        let titlePeriods;
        let titleExtraPart;
        let selectedTitlePeriod;

        if (isSurveyForDegreeProgram(surveyType)) {
          titlePeriods = getTitlePeriods();
          titleExtraPart = '';
          selectedTitlePeriod = 0;
        }

        const conclusion = schema.conclusion || getMessages(`defaultSurveyConclusions.${surveyType}`);
        const researchAgreement = getLocalizedResearchAgreement(surveyType);

        dispatchUpdate({
          schemaId,
          surveyType,
          titlePeriods,
          selectedTitlePeriod,
          title,
          schemaTitle,
          schemaVersion,
          titleExtraPart,
          introduction,
          conclusion,
          researchAgreement,
          invitationEmailTitle,
          invitationEmailContent,
          ...(invitationEmailTitle && { defaultInvitationEmailTitle: invitationEmailTitle }),
          ...(invitationEmailContent && { defaultInvitationEmailContent: invitationEmailContent })
        });

        dispatchSetTaskFulfilled();
        navigate(getSubRouteForSurveyType(surveyType).startSurvey.contents.index);
      })
      .catch((e) => {
        dispatchAddErrorMessage(e);
        dispatchSetTaskFulfilled();
      });
  };

  return (
    <>
      {React.cloneElement(children, {
        onClick: () => setIsOpen(true)
      })}
      <ModalDialog
        isOpen={isOpen}
        isLarge={false}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        cancelTranslationKey="common.cancel"
        confirmTranslationKey="common.continue"
        isValid={!!selectedSchemaId}
      >
        <h3>{t('selectSchemaDialog.title', { schemaType: t(`surveyType.${schemaType}`) })}</h3>
        <LabelWrapper label={t('selectSchemaDialog.selectSchema')}>
          <Select
            options={schemas.map(({ id, title }) => ({
              value: id,
              label: getLocalizedText(title, lang, t('surveyParts.titleMissing'))
            }))}
            placeholderKey="common.select"
            value={selectedSchemaId}
            onChange={(value) => setSelectedSchemaId(value)}
          />
        </LabelWrapper>
      </ModalDialog>
    </>
  );
};

SelectSchemaDialog.propTypes = {
  schemaType: PropTypes.string.isRequired,
  children: PropTypes.element.isRequired,
  dispatchUpdate: PropTypes.func.isRequired,
  dispatchSetTaskPending: PropTypes.func.isRequired,
  dispatchSetTaskFulfilled: PropTypes.func.isRequired,
  dispatchAddErrorMessage: PropTypes.func.isRequired,
  dispatchClearSurvey: PropTypes.func.isRequired,
  ...langType
};

const mapStateToProps = ({ user }) => ({
  organization: user.currentUser.organization.name
});

const mapDispatchToProps = (dispatch) => ({
  dispatchUpdate: (surveyOptions) => dispatch(updateSurveyOptionsAction(surveyOptions)),
  dispatchSetTaskPending: () => dispatch(setTaskPending()),
  dispatchSetTaskFulfilled: () => dispatch(setTaskFulfilled()),
  dispatchAddErrorMessage: (err) => dispatch(addErrorMessageAction(err)),
  dispatchClearSurvey: () => dispatch(clearSurveyAction())
});

export default withLang(connect(mapStateToProps, mapDispatchToProps)(SelectSchemaDialog));
